/**
 * useResponsive - Hook for responsive design
 * Provides screen size detection and responsive utilities aligned with the theme breakpoints
 */

import { useState, useEffect, useCallback } from 'react';

// Breakpoint values aligned with @turn-connect/ui theme
const BREAKPOINTS = {
  xs: 0,
  sm: 576,
  md: 768,
  lg: 992,
  xl: 1200,
  xxl: 1400,
} as const;

export type BreakpointKey = keyof typeof BREAKPOINTS;

export interface ResponsiveState {
  // Current breakpoint
  breakpoint: BreakpointKey;

  // Screen width
  width: number;

  // Device type flags
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;

  // Orientation
  isPortrait: boolean;
  isLandscape: boolean;

  // Breakpoint checks
  isXs: boolean;
  isSm: boolean;
  isMd: boolean;
  isLg: boolean;
  isXl: boolean;
  isXxl: boolean;

  // Up checks (min-width)
  isSmUp: boolean;
  isMdUp: boolean;
  isLgUp: boolean;
  isXlUp: boolean;
  isXxlUp: boolean;

  // Down checks (max-width)
  isSmDown: boolean;
  isMdDown: boolean;
  isLgDown: boolean;
  isXlDown: boolean;
}

/**
 * Get current breakpoint based on window width
 */
const getBreakpoint = (width: number): BreakpointKey => {
  if (width >= BREAKPOINTS.xxl) return 'xxl';
  if (width >= BREAKPOINTS.xl) return 'xl';
  if (width >= BREAKPOINTS.lg) return 'lg';
  if (width >= BREAKPOINTS.md) return 'md';
  if (width >= BREAKPOINTS.sm) return 'sm';
  return 'xs';
};

/**
 * Get responsive state based on window dimensions
 */
const getResponsiveState = (): ResponsiveState => {
  const width = typeof window !== 'undefined' ? window.innerWidth : 1024;
  const height = typeof window !== 'undefined' ? window.innerHeight : 768;
  const breakpoint = getBreakpoint(width);

  return {
    breakpoint,
    width,

    // Device type
    isMobile: width < BREAKPOINTS.md,
    isTablet: width >= BREAKPOINTS.md && width < BREAKPOINTS.lg,
    isDesktop: width >= BREAKPOINTS.lg,

    // Orientation
    isPortrait: height > width,
    isLandscape: width >= height,

    // Exact breakpoint
    isXs: breakpoint === 'xs',
    isSm: breakpoint === 'sm',
    isMd: breakpoint === 'md',
    isLg: breakpoint === 'lg',
    isXl: breakpoint === 'xl',
    isXxl: breakpoint === 'xxl',

    // Min-width (up)
    isSmUp: width >= BREAKPOINTS.sm,
    isMdUp: width >= BREAKPOINTS.md,
    isLgUp: width >= BREAKPOINTS.lg,
    isXlUp: width >= BREAKPOINTS.xl,
    isXxlUp: width >= BREAKPOINTS.xxl,

    // Max-width (down)
    isSmDown: width < BREAKPOINTS.md,
    isMdDown: width < BREAKPOINTS.lg,
    isLgDown: width < BREAKPOINTS.xl,
    isXlDown: width < BREAKPOINTS.xxl,
  };
};

/**
 * Hook for responsive design
 * Returns current screen state and responsive utilities
 *
 * @example
 * const { isMobile, isTablet, isDesktop, breakpoint } = useResponsive();
 *
 * // Conditional rendering
 * if (isMobile) return <MobileView />;
 * if (isTablet) return <TabletView />;
 * return <DesktopView />;
 *
 * // Style variations
 * const columns = isMobile ? 1 : isTablet ? 2 : 4;
 */
export const useResponsive = (): ResponsiveState => {
  const [state, setState] = useState<ResponsiveState>(getResponsiveState);

  const handleResize = useCallback(() => {
    const newState = getResponsiveState();
    setState((prevState) => {
      // Only update if breakpoint or device type changed to avoid unnecessary rerenders
      if (
        prevState.breakpoint !== newState.breakpoint ||
        prevState.isMobile !== newState.isMobile ||
        prevState.isTablet !== newState.isTablet ||
        prevState.isDesktop !== newState.isDesktop ||
        prevState.isPortrait !== newState.isPortrait
      ) {
        return newState;
      }
      return prevState;
    });
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Set initial state
    handleResize();

    // Add resize listener with debounce
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    const debouncedResize = () => {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(handleResize, 100);
    };

    window.addEventListener('resize', debouncedResize);
    window.addEventListener('orientationchange', handleResize);

    return () => {
      window.removeEventListener('resize', debouncedResize);
      window.removeEventListener('orientationchange', handleResize);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [handleResize]);

  return state;
};

/**
 * Utility to generate responsive styles
 * Returns style object based on current breakpoint
 *
 * @example
 * const style = getResponsiveStyle(breakpoint, {
 *   xs: { padding: 8 },
 *   md: { padding: 16 },
 *   lg: { padding: 24 },
 * });
 */
export const getResponsiveStyle = <T extends Record<string, unknown>>(
  breakpoint: BreakpointKey,
  styles: Partial<Record<BreakpointKey, T>>
): T => {
  const breakpointOrder: BreakpointKey[] = ['xs', 'sm', 'md', 'lg', 'xl', 'xxl'];
  const currentIndex = breakpointOrder.indexOf(breakpoint);

  // Find the closest defined style at or below current breakpoint
  let result: T = {} as T;
  for (let i = currentIndex; i >= 0; i--) {
    const key = breakpointOrder[i];
    if (styles[key]) {
      result = { ...result, ...styles[key] };
      break;
    }
  }

  // Apply any higher breakpoint overrides
  for (let i = 0; i <= currentIndex; i++) {
    const key = breakpointOrder[i];
    if (styles[key]) {
      result = { ...result, ...styles[key] };
    }
  }

  return result;
};

/**
 * CSS media query strings for use with CSS-in-JS
 */
export const mediaQueries = {
  mobileOnly: '@media (max-width: 767px)',
  tabletOnly: '@media (min-width: 768px) and (max-width: 991px)',
  desktopOnly: '@media (min-width: 992px)',
  mobileAndTablet: '@media (max-width: 991px)',
  tabletAndDesktop: '@media (min-width: 768px)',
  portrait: '@media (orientation: portrait)',
  landscape: '@media (orientation: landscape)',
};

export default useResponsive;
